import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import ProductTable from "@/components/ProductTable";
import ProductGrid from "@/components/ProductGrid";
import ProductDrawer from "@/components/ProductDrawer";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { buildFieldTypeMap, TABLE_COLUMNS_BY_LAYOUT, canSeeGT } from "@/lib/fmUtils";
import { useDashboardProducts, LAYOUT, LIMIT } from "@/hooks/useDashboardProducts";
import { useDashboardCarts } from "@/hooks/useDashboardCarts";
import { useDashboardSync } from "@/hooks/useDashboardSync";
import { useDashboardExport } from "@/hooks/useDashboardExport";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { DashboardActionMenu } from "@/components/dashboard/DashboardActionMenu";
import { DashboardNewCartDialog } from "@/components/dashboard/DashboardNewCartDialog";
import { BestSellingLayoutPickerDialog } from "@/components/dashboard/BestSellingLayoutPickerDialog";
import { DashboardFilterBar } from "@/components/dashboard/DashboardFilterBar";
import { DashboardSidebar } from "@/components/dashboard/DashboardSidebar";
import { DashboardPagination } from "@/components/dashboard/DashboardPagination";
import { Button } from "@/components/ui/button";
import { ShoppingCartSimple, CircleNotch } from "@phosphor-icons/react";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  // Selections stored as an ordered array of full records. Using an array
  // (instead of a plain object keyed by recordId) is deliberate: recordIds are
  // strings of positive integers, which ECMAScript treats as canonical array
  // indices - meaning `Object.values(obj)` returns them in ASCENDING NUMERIC
  // order, not click-insertion order. An array preserves the exact click order
  // and makes the Best Selling drawer's prev/next cycle predictable.
  const [selectedList, setSelectedList] = useState([]);
  const [addingToQuote, setAddingToQuote] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [exportCustomerId, setExportCustomerId] = useState("none");
  const [bsLayoutPickerOpen, setBsLayoutPickerOpen] = useState(false);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const exportCustomer = customers.find((c) => c.id === exportCustomerId);
  const exportCoefficient = exportCustomer?.coefficient || 1;

  // Persist Best Selling selections per user across navigation & printing.
  // Stored in localStorage keyed by user id/email so different users on the
  // same browser do NOT read each other's selections.
  const userKey = user?.id || user?.email || "anon";
  const bsSelectedStorageKey = `bs_selected_${userKey}`;
  const lastViewStorageKey = `dash_view_${userKey}`;

  const p = useDashboardProducts();
  const carts = useDashboardCarts();
  const sync = useDashboardSync(p.fetchRecords);
  const isBestSelling = p.view === "best_selling";
  const isCustomer = user?.role === "customer";

  // Restore the last active dashboard view on mount so that returning from
  // /best-selling/print via Back lands the user back on Best Selling instead
  // of the default Inventory view.
  const viewRestoredRef = useRef(false);
  useEffect(() => {
    if (viewRestoredRef.current) return;
    viewRestoredRef.current = true;
    try {
      const stored = localStorage.getItem(lastViewStorageKey);
      if (stored && stored !== p.view) {
        p.handleViewChange(stored);
      }
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastViewStorageKey]);

  // Persist active view whenever it changes.
  useEffect(() => {
    try {
      localStorage.setItem(lastViewStorageKey, p.view);
    } catch {
      /* ignore */
    }
  }, [p.view, lastViewStorageKey]);

  // Sync `selectedList` with the persisted store when the Best Selling view is
  // entered/left. Only best_selling has persistent selections. Reads support
  // both the new array-of-records shape AND the legacy plain-object shape
  // (previous versions of the app persisted `{recordId: rec, ...}`) so users
  // who had selections stored before this refactor don't lose them.
  useEffect(() => {
    if (isBestSelling) {
      try {
        const raw = localStorage.getItem(bsSelectedStorageKey);
        if (!raw) {
          setSelectedList([]);
        } else {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            setSelectedList(parsed);
          } else if (parsed && typeof parsed === "object") {
            // Legacy shape: object keyed by recordId. Values() will iterate in
            // numeric-ascending order but at least the selections are preserved.
            setSelectedList(Object.values(parsed));
          } else {
            setSelectedList([]);
          }
        }
      } catch {
        setSelectedList([]);
      }
    } else {
      setSelectedList([]);
      setShowSelectedOnly(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBestSelling, bsSelectedStorageKey]);

  // Save Best Selling selections whenever they change.
  useEffect(() => {
    if (!isBestSelling) return;
    try {
      localStorage.setItem(bsSelectedStorageKey, JSON.stringify(selectedList));
    } catch {
      /* storage full or blocked; ignore */
    }
  }, [selectedList, isBestSelling, bsSelectedStorageKey]);

  useEffect(() => {
    if (isCustomer) return;
    api
      .get("/customers")
      .then(({ data }) => setCustomers(data))
      .catch(() => {});
  }, [isCustomer]);

  const exportFilters = {
    search: p.search,
    category: p.category,
    cbmMin: p.cbmMin,
    cbmMax: p.cbmMax,
    qtyMin: p.qtyMin,
    qtyMax: p.qtyMax,
    dateFrom: p.dateFrom,
    dateTo: p.dateTo,
    sortField: p.sortField,
    sortOrder: p.sortOrder,
    view: p.view,
    showAll: p.showAll,
    tag: p.tag,
    coefficient: exportCoefficient,
  };
  const { exportingProducts, handleExportProducts } = useDashboardExport(exportFilters);

  const handleAddSelectedToCart = async () => {
    const recordIds = selectedList.map((r) => r.recordId);
    let added = 0;
    for (const recordId of recordIds) {
      try {
        await api.post("/cart/items", { main_product_record_id: recordId });
        added += 1;
      } catch {
        /* duplicates / failures counted below */
      }
    }
    toast({
      title: `Added ${added} of ${recordIds.length} product(s) to cart`,
      ...(added < recordIds.length ? { description: "Some items were already in the cart or could not be added." } : {}),
    });
    setSelectedList([]);
    carts.fetchCarts();
  };

  const handlePrintGridView = () => {
    sessionStorage.setItem("productGridPrintRecords", JSON.stringify(selectedList));
    navigate("/products/print");
  };

  const handleGenerateLink = async (coefficient) => {
    try {
      const params = { coefficient };
      // Generate Link now operates on the user's selected products ONLY, not the current
      // filter window - so pass the selected recordIds in the body and skip filter params.
      const body = { record_ids: selectedList.map((r) => r.recordId) };
      const { data } = await api.post("/fm/products/share-link", body, { params });
      toast({ title: `Link created (${data.product_count} products)` });
      return data.link;
    } catch (e) {
      toast({
        title: "Could not generate link",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
      return null;
    }
  };

  const fieldTypeMap = useMemo(() => buildFieldTypeMap(p.fields), [p.fields]);
  const columns = useMemo(() => TABLE_COLUMNS_BY_LAYOUT[LAYOUT] || [], []);
  const gridColumns = useMemo(
    () => (isBestSelling ? columns : columns.filter((c) => !["category", "date"].includes(c.key))),
    [columns, isBestSelling]
  );
  const tableColumns = useMemo(
    () => (isBestSelling ? columns : columns.filter((c) => c.key !== "category")),
    [columns, isBestSelling]
  );
  // Only wait on the actual records fetch. Previously this also required
  // `fields.length > 0`, but that ties the Dashboard's render state to the
  // FileMaker layout-fields metadata call - which can fail if FileMaker is
  // temporarily unreachable, leaving the whole page stuck on the loading
  // skeleton forever. The MongoDB product cache is the source of truth for
  // rendering the grid/table; layout-fields is only used to detect container
  // vs text columns and gracefully degrades when empty.
  const tableLoading = p.loading;

  const openRecordAndDrawer = (rec, idx) => {
    p.openRecord(rec, idx);
    setDrawerOpen(true);
  };

  const handleAddToCart = async (rec) => {
    try {
      await api.post("/cart/items", { main_product_record_id: rec.recordId });
      toast({ title: "Added to cart" });
      carts.fetchCarts();
    } catch (e) {
      toast({
        title: "Could not add to cart",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    }
  };

  const selectedIds = useMemo(() => new Set(selectedList.map((r) => r.recordId)), [selectedList]);

  // When "Show Selected Only" is ON in Best Selling view, replace the paginated
  // server records with the full set of selected records (which we have cached
  // in state). This lets the user review their persistent picks even when they
  // span multiple pages.
  const effectiveRecords = useMemo(() => {
    if (isBestSelling && showSelectedOnly) return selectedList;
    return p.records;
  }, [isBestSelling, showSelectedOnly, selectedList, p.records]);

  const handleClearSelection = () => {
    setSelectedList([]);
    setShowSelectedOnly(false);
    try {
      localStorage.removeItem(bsSelectedStorageKey);
    } catch {
      /* ignore */
    }
  };

  // Best Selling drawer prev/next: when there are selections and the currently
  // open record is one of them, cycle through the selected records IN THE ORDER
  // they were selected (selectedList is an array so click-insertion order is
  // guaranteed - see the state declaration above for why an object won't do).
  // Falls back to the default paginated navigation when the view isn't Best
  // Selling or the open record isn't in the selected set.
  const selectedNavIndex =
    isBestSelling && p.selectedRecord
      ? selectedList.findIndex((r) => r.recordId === p.selectedRecord.recordId)
      : -1;
  const useSelectedNav = isBestSelling && selectedList.length > 0 && selectedNavIndex >= 0;
  const canGoPrevOverride = useSelectedNav ? selectedNavIndex > 0 : p.canGoPrev;
  const canGoNextOverride = useSelectedNav
    ? selectedNavIndex < selectedList.length - 1
    : p.canGoNext;
  const handleNavigateOverride = (direction) => {
    if (!useSelectedNav) return p.handleNavigate(direction);
    if (direction === "next" && selectedNavIndex < selectedList.length - 1) {
      p.openRecord(selectedList[selectedNavIndex + 1], null);
    } else if (direction === "prev" && selectedNavIndex > 0) {
      p.openRecord(selectedList[selectedNavIndex - 1], null);
    }
  };

  const handleViewChange = (view) => {
    if (view === "cart") {
      navigate("/cart");
      return;
    }
    if (view === "customers") {
      navigate("/customers");
      return;
    }
    if (view === "help") {
      navigate("/help");
      return;
    }
    if (view === "bh_history") {
      navigate("/bh-selling-history");
      return;
    }
    // Selection is reset (or restored, for best_selling) by the useEffect
    // that watches `isBestSelling`.
    p.handleViewChange(view);
  };

  const handleToggleSelect = (rec) => {
    setSelectedList((prev) => {
      const idx = prev.findIndex((r) => r.recordId === rec.recordId);
      if (idx >= 0) {
        // Unselect: keep order of remaining items intact.
        return prev.filter((_, i) => i !== idx);
      }
      // Select: append so the newest click sits at the end (natural cycle order).
      return [...prev, rec];
    });
  };

  const handleSelectAll = () => {
    setSelectedList((prev) => {
      const existing = new Set(prev.map((r) => r.recordId));
      const additions = p.records.filter((rec) => !existing.has(rec.recordId));
      return [...prev, ...additions];
    });
  };

  const handlePrintSelected = () => {
    if (!selectedList.length) return;
    setBsLayoutPickerOpen(true);
  };

  const handlePickBsLayout = (layoutKey) => {
    if (!selectedList.length) return;
    sessionStorage.setItem("bestSellingPrintRecords", JSON.stringify(selectedList));
    sessionStorage.setItem("bestSellingPrintLayout", layoutKey);
    navigate("/best-selling/print");
  };

  return (
    <div className="flex h-screen w-screen flex-col overflow-x-hidden bg-white" data-testid="dashboard-page">
      <DashboardHeader
        user={user}
        isAdmin={user?.role === "admin"}
        canOpenSettings={user?.role === "admin" || user?.role === "office"}
        carts={carts.carts}
        activeCart={carts.activeCart}
        onActivateCart={carts.handleActivateCart}
        onNewCart={() => carts.setNewCartOpen(true)}
        onLogout={logout}
        onNavigate={navigate}
        hideCart={isBestSelling}
      />

      <DashboardNewCartDialog
        open={carts.newCartOpen}
        onOpenChange={carts.setNewCartOpen}
        name={carts.newCartName}
        onNameChange={carts.setNewCartName}
        saving={carts.creatingCart}
        onSubmit={carts.handleCreateCart}
      />

      <BestSellingLayoutPickerDialog
        open={bsLayoutPickerOpen}
        onOpenChange={setBsLayoutPickerOpen}
        onPick={handlePickBsLayout}
      />

      <div className="flex min-h-0 flex-1">
        <DashboardSidebar
          activeView={p.view}
          onViewChange={handleViewChange}
          showBestSelling={canSeeGT(user)}
          showCustomers={user?.role === "admin"}
          showBHHistory={user?.role === "admin" || user?.role === "office"}
          cartName={carts.activeCart?.customer_name}
          cartItemCount={carts.activeCart?.item_count}
        />

        <div className="flex min-w-0 flex-1 flex-col">
          <DashboardFilterBar
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            categoryOptions={p.categoryOptions}
            category={p.category}
            onCategoryChange={p.handleCategoryChange}
            subCategoryOptions={p.subCategoryOptions}
            subCategory={p.subCategory}
            onSubCategoryChange={p.handleSubCategoryChange}
            sortField={p.sortField}
            onSortFieldChange={p.handleSortFieldChange}
            sortOrder={p.sortOrder}
            onToggleSortOrder={p.toggleSortOrder}
            cacheStatus={sync.cacheStatus}
            showAll={p.showAll}
            onToggleShowAll={p.toggleShowAll}
            showBestSellingFilter={canSeeGT(user)}
            bestOnlyFilter={p.bestOnly}
            onToggleBestOnly={p.toggleBestOnly}
            showBlockedFilter={user?.role === "admin" || user?.role === "office"}
            blockedOnlyFilter={p.blockedOnly}
            onToggleBlockedOnly={p.toggleBlockedOnly}
            bestSellingMode={isBestSelling}
            showSelectionButtons={!isCustomer}
            showGTHistory={canSeeGT(user)}
            selectedCount={selectedIds.size}
            onSelectAll={handleSelectAll}
            onClearSelection={handleClearSelection}
            onPrint={handlePrintSelected}
            showSelectedOnlyToggle={isBestSelling}
            showSelectedOnly={showSelectedOnly}
            onToggleShowSelectedOnly={() => setShowSelectedOnly((v) => !v)}
            onOpenGTHistory={() => navigate("/gt-selling-history")}
            searchInput={p.searchInput}
            onSearchInputChange={p.setSearchInput}
            cbmMinInput={p.cbmMinInput}
            onCbmMinChange={p.setCbmMinInput}
            cbmMaxInput={p.cbmMaxInput}
            onCbmMaxChange={p.setCbmMaxInput}
            qtyMinInput={p.qtyMinInput}
            onQtyMinChange={p.setQtyMinInput}
            qtyMaxInput={p.qtyMaxInput}
            onQtyMaxChange={p.setQtyMaxInput}
            dateFromInput={p.dateFromInput}
            onDateFromChange={p.setDateFromInput}
            dateToInput={p.dateToInput}
            onDateToChange={p.setDateToInput}
            onSearchSubmit={p.handleSearchSubmit}
            actionSlot={
              user?.role === "sales" || user?.role === "gt_sales" ? (
                <Button
                  size="sm"
                  disabled={selectedIds.size === 0 || addingToQuote}
                  onClick={async () => {
                    setAddingToQuote(true);
                    try {
                      await handleAddSelectedToCart();
                    } finally {
                      setAddingToQuote(false);
                    }
                  }}
                  className="rounded-sm bg-blue-600 text-white hover:bg-blue-700"
                  data-testid="products-add-to-quote-button"
                >
                  {addingToQuote ? (
                    <CircleNotch size={14} className="mr-1.5 animate-spin" />
                  ) : (
                    <ShoppingCartSimple size={14} className="mr-1.5" />
                  )}
                  Add to Quote{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
                </Button>
              ) : user?.role !== "customer" ? (
                <DashboardActionMenu
                  isCustomer={isCustomer}
                  exporting={exportingProducts}
                  onExport={handleExportProducts}
                  selectedCount={selectedIds.size}
                  onAddSelectedToCart={handleAddSelectedToCart}
                  onPrintGridView={handlePrintGridView}
                  onGenerateLink={handleGenerateLink}
                  customers={customers}
                  exportCustomerId={exportCustomerId}
                  onExportCustomerChange={setExportCustomerId}
                />
              ) : null
            }
          />

          <div className="min-w-0 flex-1 overflow-auto">
            {viewMode === "grid" ? (
              <ProductGrid
                layout={LAYOUT}
                columns={gridColumns}
                records={effectiveRecords}
                fieldTypeMap={fieldTypeMap}
                loading={tableLoading}
                onRowClick={openRecordAndDrawer}
                onAddToCart={handleAddToCart}
                photoVersion={sync.photoCacheBust}
                selectable={isBestSelling || !isCustomer}
                selectedIds={selectedIds}
                onToggleSelect={handleToggleSelect}
                bestSellingLayout={isBestSelling}
                showBestBadge={canSeeGT(user) && !isBestSelling}
                showHoverPreview={isBestSelling}
                showVariantSummary={!isBestSelling}
              />
            ) : (
              <ProductTable
                layout={LAYOUT}
                columns={tableColumns}
                records={effectiveRecords}
                fieldTypeMap={fieldTypeMap}
                loading={tableLoading}
                onRowClick={openRecordAndDrawer}
                onAddToCart={handleAddToCart}
                photoVersion={sync.photoCacheBust}
                selectable={isBestSelling || !isCustomer}
                selectedIds={selectedIds}
                onToggleSelect={handleToggleSelect}
                showHoverPreview={isBestSelling}
                showBestBadge={canSeeGT(user) && !isBestSelling}
                showVariantSummary={!isBestSelling}
              />
            )}
          </div>

          <DashboardPagination
            foundCount={isBestSelling && showSelectedOnly ? effectiveRecords.length : p.foundCount}
            offset={p.offset}
            limit={LIMIT}
            currentPage={p.currentPage}
            totalPages={p.totalPages}
            onPrev={() => p.setOffset(Math.max(1, p.offset - LIMIT))}
            onNext={() => p.setOffset(p.offset + LIMIT)}
            hideNav={isBestSelling && showSelectedOnly}
          />
        </div>
      </div>

      <ProductDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        layout={LAYOUT}
        record={p.selectedRecord}
        fields={p.fields}
        bestSelling={isBestSelling}
        onSaved={p.fetchRecords}
        onCartUpdated={carts.fetchCarts}
        onNavigate={handleNavigateOverride}
        canGoPrev={canGoPrevOverride}
        canGoNext={canGoNextOverride}
        photoVersion={sync.photoCacheBust}
        onPhotoUploaded={() => sync.setPhotoCacheBust(Date.now())}
      />
    </div>
  );
}
